import type { SVGProps } from 'react';
import { cx } from '../cx';

/**
 * Ícones do design system: traço 1.5, 24×24, cor herdada. Sem emoji na interface.
 * Novo ícone = nova entrada aqui (paths em viewBox 0 0 24 24).
 */
const PATHS = {
  mic: 'M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3zM5 11a7 7 0 0 0 14 0M12 18v3M8 21h8',
  'mic-off': 'M15 9.5V6a3 3 0 0 0-5.6-1.5M9 9v3a3 3 0 0 0 5.1 2.1M5 11a7 7 0 0 0 11.4 5.4M19 11a7 7 0 0 1-.6 2.8M12 18v3M8 21h8M3 3l18 18',
  volume: 'M4 9.5v5h3.5L12 18.5v-13L7.5 9.5H4zM15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11',
  'volume-off': 'M4 9.5v5h3.5L12 18.5v-13L7.5 9.5H4zM16 9.5l5 5M21 9.5l-5 5',
  paperclip: 'M20.5 11.5 12 20a5 5 0 0 1-7-7l8.5-8.5a3.5 3.5 0 0 1 5 5L10 18a2 2 0 0 1-3-3l7.5-7.5',
  x: 'M6 6l12 12M18 6 6 18',
  check: 'M5 12.5l4.5 4.5L19 7',
  'check-double': 'M2.5 12.5l4 4L13 10M9.5 16.5l1 1L21 7',
  heart: 'M12 20s-7.5-4.6-7.5-10A4 4 0 0 1 12 7.6 4 4 0 0 1 19.5 10c0 5.4-7.5 10-7.5 10z',
  comment: 'M4 5.5h16v10H9l-5 4v-4H4z',
  send: 'M21 3 10.5 13.5M21 3l-6.5 18-4-7.5L3 9.5 21 3z',
  bookmark: 'M6 3.5h12v17l-6-4-6 4z',
  'chevron-down': 'M6 9l6 6 6-6',
  search: 'M10.5 4a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13zM15.5 15.5 21 21',
  pencil: 'M4 20l4.5-1L19 8.5a2.1 2.1 0 0 0-3-3L5.5 16 4 20zM13.5 8l3 3',
  plus: 'M12 5v14M5 12h14',
  smile: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM8.5 14.5a4.5 4.5 0 0 0 7 0M9 9.5v.5M15 9.5v.5',
  stop: 'M7 7h10v10H7z',
  dot: 'M12 8a4 4 0 1 1 0 8 4 4 0 0 1 0-8z',
  image: 'M4 5.5h16v13H4zM4 15l5-4.5 4 3.5 3-2.5 4 3.5M15.5 9.5h.01',
  video: 'M3.5 7h11v10h-11zM14.5 10.5 20.5 8v8l-6-2.5',
  bell: 'M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15zM10 20.5a2 2 0 0 0 4 0',
  user: 'M12 4a4 4 0 1 1 0 8 4 4 0 0 1 0-8zM4.5 20a7.5 7.5 0 0 1 15 0',
  phone: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z',
  'arrow-right': 'M4 12h16M14 6l6 6-6 6',
  play: 'M7 4.5v15l12-7.5z',
  rect: 'M4 6h16v12H4z',
  'chevron-up': 'M6 15l6-6 6 6',
  file: 'M6.5 3.5h7l4 4v13h-11zM13.5 3.5v4h4M9 12.5h6M9 16h6',
  table: 'M4 5.5h16v13H4zM4 10h16M4 14.5h16M10 5.5v13',
  slides: 'M3.5 5h17v11h-17zM12 16v4M8 20h8M8 9.5h8M8 12.5h5',
  link: 'M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3A4 4 0 0 0 11 18.7l1-1',
} as const;
export type IconName = keyof typeof PATHS;

export function Icon({ name, size = 16, className, strokeWidth = 1.5, filled, ...rest }: { name: IconName; size?: number; strokeWidth?: number; /** preenche a forma (ex.: coração marcado) */ filled?: boolean } & Omit<SVGProps<SVGSVGElement>, 'name'>) {
  return (
    <svg className={cx('rr-icon', className)} width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false" {...rest}>
      <path d={PATHS[name]} />
    </svg>
  );
}
